import { Disclosure, Transition } from '@headlessui/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import get from 'lodash/get';
import { JSX, useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { UsersSelectPaginated } from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { UpdateTaskModal } from '@/modules/admin-dashboard/components/modals/UpdateTaskModal.tsx';
import { useTasksActivityLogsQuery } from '@/modules/admin-dashboard/queries/tasks.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import {
  Button,
  CalendarInput,
  CalendarInputProps,
  InfoCard,
  ModalProviderContext,
  Select,
  SelectOption,
  SelectPaginateOption,
  Skeleton,
  Spinner,
} from 'ui';
import {
  AlarmClockGreyIcon,
  AlertGrayIcon,
  CalendarGreyIcon,
  CheckGray,
  ChevronsRightGrayIcon,
  EditGrayIcon,
  FileGreyIcon,
  FunnelBlueIcon,
  HistoryGrayIcon,
  PieChartGeyIcon,
  PlusGrayIcon,
  SendGreyIcon,
  TaskGreyIcon,
  TrashGrayIcon,
  UploadGrayIcon,
  UsersAddGrayIcon,
} from 'ui/icons';
import { ActivityType } from 'compliance-api';

export const RecentActivitySkeleton = () => {
  return (
    <div className="flex flex-col gap-[17px] py-[1px]">
      {Array(5)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="flex gap-4">
            <Skeleton width={36} height={36} borderRadius={36} />
            <div className="flex flex-col gap-2">
              <Skeleton width={301} height={13} />
              <Skeleton width={101} height={13} />
            </div>
          </div>
        ))}
    </div>
  );
};

const allActivitySelectOptions: SelectOption<ActivityType>[] = [
  { label: 'Task created', value: ActivityType.TaskCreated },
  { label: 'Task updated', value: ActivityType.TaskUpdated },
  { label: 'Task deleted', value: ActivityType.TaskDeleted },
  { label: 'Task result created', value: ActivityType.TaskResultCreated },
  { label: 'Task result updated', value: ActivityType.TaskResultUpdated },
  { label: 'Task result deleted', value: ActivityType.TaskResultDeleted },
  { label: 'Compliance status changed', value: ActivityType.RegisterStatusChanged },
  { label: 'Due reminder finished', value: ActivityType.DueReminderFinished },
  { label: 'Event type created', value: ActivityType.IncidentTypeCreated },
  { label: 'Event type updated', value: ActivityType.IncidentTypeUpdated },
  { label: 'Event type deleted', value: ActivityType.IncidentTypeDeleted },
  { label: 'Event created', value: ActivityType.IncidentResultCreated },
  { label: 'Event updated', value: ActivityType.IncidentResultUpdated },
  { label: 'Event deleted', value: ActivityType.IncidentResultDeleted },
  { label: 'Event task completed', value: ActivityType.IncidentTaskCompleted },
  { label: 'Document uploaded', value: ActivityType.DocumentUploaded },
  { label: 'Document updated', value: ActivityType.DocumentUpdated },
  { label: 'Document moved', value: ActivityType.DocumentMoved },
  { label: 'Document deleted', value: ActivityType.DocumentDeleted },
  { label: 'Document folder created', value: ActivityType.DocumentFolderCreated },
  { label: 'Document folder updated', value: ActivityType.DocumentFolderUpdated },
  { label: 'Document folder moved', value: ActivityType.DocumentFolderMoved },
  { label: 'Document folder deleted', value: ActivityType.DocumentFolderDeleted },
  { label: 'Form created', value: ActivityType.FormCreated },
  { label: 'Form updated', value: ActivityType.FormUpdated },
  { label: 'Form deleted', value: ActivityType.FormDeleted },
  { label: 'Register created', value: ActivityType.RegisterCreated },
  { label: 'Register updated', value: ActivityType.RegisterUpdated },
  { label: 'Register deleted', value: ActivityType.RegisterDeleted },
  { label: 'Department created', value: ActivityType.DepartmentCreated },
  { label: 'Department updated', value: ActivityType.DepartmentUpdated },
  { label: 'Department deleted', value: ActivityType.DepartmentDeleted },
  { label: 'Report created', value: ActivityType.ReportCreated },
  { label: 'Report deleted', value: ActivityType.ReportDeleted },
  { label: 'Client created', value: ActivityType.ClientCreated },
  { label: 'Client updated', value: ActivityType.ClientUpdated },
  { label: 'Client deleted', value: ActivityType.ClientDeleted },
  { label: 'Communication created', value: ActivityType.CommunicationCreated },
  { label: 'Email sent', value: ActivityType.EmailSent },
  { label: 'Email received', value: ActivityType.EmailReceived },
  { label: 'User added', value: ActivityType.UserAdded },
  { label: 'Meeting scheduled', value: ActivityType.MeetingScheduled },
  { label: 'Meeting updated', value: ActivityType.MeetingUpdated },
  { label: 'Meeting reminder sent', value: ActivityType.MeetingReminderSent },
  { label: 'Organisation updated', value: ActivityType.OrganisationUpdated },
  { label: 'Batch report created', value: ActivityType.BatchReportCreated },
  { label: 'Report template created', value: ActivityType.ReportTemplateCreated },
  { label: 'Vault created', value: ActivityType.VaultCreated },
  { label: 'Vault updated', value: ActivityType.VaultUpdated },
  { label: 'Vault deleted', value: ActivityType.VaultDeleted },
  { label: 'Vault shared', value: ActivityType.VaultShared },
  { label: 'Secret created', value: ActivityType.SecretCreated },
  { label: 'Secret updated', value: ActivityType.SecretUpdated },
  { label: 'Secret deleted', value: ActivityType.SecretDeleted },
  { label: 'Contract created', value: ActivityType.ContractCreated },
  { label: 'Contract updated', value: ActivityType.ContractUpdated },
  { label: 'Contract deleted', value: ActivityType.ContractDeleted },
  { label: 'Contract restored', value: ActivityType.ContractRestored },
  { label: 'Contract party created', value: ActivityType.ContractPartyCreated },
  { label: 'Contract party updated', value: ActivityType.ContractPartyUpdated },
  { label: 'Contract party deleted', value: ActivityType.ContractPartyDeleted },
  { label: 'Party created', value: ActivityType.PartyCreated },
  { label: 'Party updated', value: ActivityType.PartyUpdated },
  { label: 'Party deleted', value: ActivityType.PartyDeleted },
  { label: 'Party restored', value: ActivityType.PartyRestored },
  { label: 'Contract contributor added', value: ActivityType.ContractContributorAdded },
  {
    label: 'Contract contributor permission updated',
    value: ActivityType.ContractContributorPermissionUpdated,
  },
  { label: 'Contract contributor removed', value: ActivityType.ContractContributorRemoved },
];

const clientOnlyExcludedActivityTypes = new Set<ActivityType>([
  ActivityType.RegisterStatusChanged,
  ActivityType.DueReminderFinished,
  ActivityType.IncidentTypeCreated,
  ActivityType.IncidentTypeUpdated,
  ActivityType.IncidentTypeDeleted,
  ActivityType.FormCreated,
  ActivityType.FormUpdated,
  ActivityType.FormDeleted,
  ActivityType.RegisterCreated,
  ActivityType.RegisterUpdated,
  ActivityType.RegisterDeleted,
  ActivityType.DepartmentCreated,
  ActivityType.DepartmentUpdated,
  ActivityType.DepartmentDeleted,
  ActivityType.VaultCreated,
  ActivityType.VaultUpdated,
  ActivityType.VaultDeleted,
  ActivityType.VaultShared,
  ActivityType.SecretCreated,
  ActivityType.SecretUpdated,
  ActivityType.SecretDeleted,
  ActivityType.MeetingScheduled,
  ActivityType.MeetingUpdated,
  ActivityType.MeetingReminderSent,
  ActivityType.OrganisationUpdated,
  ActivityType.BatchReportCreated,
  ActivityType.ReportTemplateCreated,
  ActivityType.UserAdded,
]);

export const activitySelectOptions = allActivitySelectOptions;

export const clientActivitySelectOptions = allActivitySelectOptions.filter(
  option => !clientOnlyExcludedActivityTypes.has(option.value),
);

export const mapActivityTypeToIcon: Partial<Record<ActivityType, string>> = {
  [ActivityType.TaskCreated]: TaskGreyIcon,
  [ActivityType.TaskUpdated]: HistoryGrayIcon,
  [ActivityType.TaskDeleted]: TrashGrayIcon,
  [ActivityType.TaskResultCreated]: CheckGray,
  [ActivityType.RegisterStatusChanged]: PieChartGeyIcon,
  [ActivityType.DueReminderFinished]: AlarmClockGreyIcon,
  [ActivityType.IncidentTypeCreated]: PlusGrayIcon,
  [ActivityType.IncidentTypeDeleted]: TrashGrayIcon,
  [ActivityType.IncidentTypeUpdated]: EditGrayIcon,
  [ActivityType.IncidentResultCreated]: AlertGrayIcon,
  [ActivityType.IncidentResultUpdated]: EditGrayIcon,
  [ActivityType.IncidentTaskCompleted]: PlusGrayIcon,
  [ActivityType.DocumentFolderCreated]: PlusGrayIcon,
  [ActivityType.DocumentFolderUpdated]: EditGrayIcon,
  [ActivityType.DocumentFolderMoved]: ChevronsRightGrayIcon,
  [ActivityType.DocumentFolderDeleted]: TrashGrayIcon,
  [ActivityType.DocumentUploaded]: UploadGrayIcon,
  [ActivityType.DocumentUpdated]: EditGrayIcon,
  [ActivityType.DocumentMoved]: ChevronsRightGrayIcon,
  [ActivityType.DocumentDeleted]: TrashGrayIcon,
  [ActivityType.IncidentResultDeleted]: TrashGrayIcon,
  [ActivityType.TaskResultUpdated]: EditGrayIcon,
  [ActivityType.TaskResultDeleted]: TrashGrayIcon,
  [ActivityType.FormCreated]: PlusGrayIcon,
  [ActivityType.FormUpdated]: EditGrayIcon,
  [ActivityType.FormDeleted]: TrashGrayIcon,
  [ActivityType.RegisterCreated]: PlusGrayIcon,
  [ActivityType.RegisterUpdated]: EditGrayIcon,
  [ActivityType.RegisterDeleted]: TrashGrayIcon,
  [ActivityType.DepartmentCreated]: PlusGrayIcon,
  [ActivityType.DepartmentUpdated]: EditGrayIcon,
  [ActivityType.DepartmentDeleted]: TrashGrayIcon,
  [ActivityType.ReportCreated]: PlusGrayIcon,
  [ActivityType.ReportDeleted]: TrashGrayIcon,
  [ActivityType.ClientCreated]: PlusGrayIcon,
  [ActivityType.ClientUpdated]: EditGrayIcon,
  [ActivityType.ClientDeleted]: TrashGrayIcon,
  [ActivityType.CommunicationCreated]: PlusGrayIcon,
  [ActivityType.EmailSent]: SendGreyIcon,
  [ActivityType.EmailReceived]: SendGreyIcon,
  [ActivityType.UserAdded]: UsersAddGrayIcon,
  [ActivityType.MeetingScheduled]: CalendarGreyIcon,
  [ActivityType.MeetingUpdated]: EditGrayIcon,
  [ActivityType.MeetingReminderSent]: SendGreyIcon,
  [ActivityType.OrganisationUpdated]: EditGrayIcon,
  [ActivityType.BatchReportCreated]: PlusGrayIcon,
  [ActivityType.ReportTemplateCreated]: FileGreyIcon,
  [ActivityType.VaultCreated]: PlusGrayIcon,
  [ActivityType.VaultUpdated]: EditGrayIcon,
  [ActivityType.VaultDeleted]: TrashGrayIcon,
  [ActivityType.VaultShared]: UsersAddGrayIcon,
  [ActivityType.SecretCreated]: FileGreyIcon,
  [ActivityType.SecretUpdated]: EditGrayIcon,
  [ActivityType.SecretDeleted]: TrashGrayIcon,
  [ActivityType.ContractCreated]: PlusGrayIcon,
  [ActivityType.ContractUpdated]: EditGrayIcon,
  [ActivityType.ContractDeleted]: TrashGrayIcon,
  [ActivityType.ContractRestored]: HistoryGrayIcon,
  [ActivityType.ContractPartyCreated]: PlusGrayIcon,
  [ActivityType.ContractPartyUpdated]: EditGrayIcon,
  [ActivityType.ContractPartyDeleted]: TrashGrayIcon,
  [ActivityType.PartyCreated]: PlusGrayIcon,
  [ActivityType.PartyUpdated]: EditGrayIcon,
  [ActivityType.PartyDeleted]: TrashGrayIcon,
  [ActivityType.PartyRestored]: HistoryGrayIcon,
  [ActivityType.ContractContributorAdded]: UsersAddGrayIcon,
  [ActivityType.ContractContributorPermissionUpdated]: EditGrayIcon,
  [ActivityType.ContractContributorRemoved]: TrashGrayIcon,
};

type ActivityTextRenderer = (
  data: object,
  options: { openModal: ModalProviderContext['openModal'] },
) => JSX.Element;

const getActionUserFullName = (data: object) =>
  `${get(data, 'action_taken_by_user.first_name', '')} ${get(
    data,
    'action_taken_by_user.last_name',
    '',
  )}`.trim();

const getFirstExistingValue = (data: object, paths: string[], fallback = '-') => {
  const value = paths.map(path => get(data, path, '')).find(Boolean);
  return value || fallback;
};

const createSimpleActivityRenderer =
  (action: string, entityName: string, labelPaths: string[]): ActivityTextRenderer =>
  (data: object) => {
    const fullName = getActionUserFullName(data);
    const label = getFirstExistingValue(data, labelPaths);

    return (
      <div>
        {fullName && <span className="font-semibold break-all">{fullName} </span>}
        {action} {entityName} <span className="font-semibold break-all">{label}</span>
      </div>
    );
  };

const contractLabelPaths = [
  'contract.contract_name',
  'contract.name',
  'contract.subject',
  'subject',
  'contract_id',
  'contract.id',
];

const partyLabelPaths = ['party.legal_name', 'party.name', 'subject', 'party_id'];

const contributorLabelPaths = [
  'contributor.user.full_name',
  'contributor.user.name',
  'contributor.name',
  'user.full_name',
  'user.name',
  'subject',
  'contributor_id',
  'user_id',
];

export const mapActivityTypeToGetTextFunction: Record<ActivityType, ActivityTextRenderer> = {
  [ActivityType.TaskCreated]: (data: object, { openModal }) => {
    const name = get(data, 'task.name', '');
    const id = get(data, 'task.id', 0);
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>
        <span> created task </span>
        <span
          className="cursor-pointer font-semibold break-all hover:underline"
          onClick={() =>
            openModal(UpdateTaskModal, {
              id,
              defaultDisabled: true,
            })
          }
        >
          {name}
        </span>
      </div>
    );
  },
  [ActivityType.TaskUpdated]: (data: object, { openModal }) => {
    const name = get(data, 'task.name', '');
    const id = get(data, 'task.id', 0);
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>
        <span> updated task </span>
        <span
          className="cursor-pointer font-semibold break-all hover:underline"
          onClick={() =>
            openModal(UpdateTaskModal, {
              id,
              defaultDisabled: true,
            })
          }
        >
          {name}
        </span>
      </div>
    );
  },
  [ActivityType.TaskDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const taskName = get(data, 'task.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted task <span className="font-semibold break-all">{taskName}</span>
      </div>
    );
  },
  [ActivityType.TaskResultCreated]: (data: object, { openModal }) => {
    const name = get(data, 'task.name', '');
    const id = get(data, 'task.id', 0);
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const reachedStatus = get(data, 'task_result.result', 'due');
    const currentStatus = get(data, 'task.overdue_date') ? 'overdue' : reachedStatus;

    return (
      <div>
        <span>Task </span>
        <span
          className="cursor-pointer font-semibold break-all hover:underline"
          onClick={() =>
            openModal(UpdateTaskModal, {
              id,
              defaultDisabled: true,
            })
          }
        >
          {name}
        </span>
        <span> of </span>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>
        <span>
          {' '}
          reached it's {reachedStatus} date and now is {currentStatus}
        </span>
      </div>
    );
  },
  [ActivityType.RegisterStatusChanged]: (data: object) => {
    //todo: delete?
    const name = get(data, 'task_instance.name', '');
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      <div>
        {fullName && (
          <>
            <span className="font-semibold break-all">{fullName} </span>
            <span>changed status of label </span>
          </>
        )}
        {!fullName && <span>Label </span>}"<span className="font-semibold break-all">{name}</span>"
        {!fullName && <span> status changed</span>}
      </div>
    );
  },
  [ActivityType.DueReminderFinished]: (data: object, { openModal }) => {
    const name = get(data, 'name', '');
    const id = get(data, 'id', 0);
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      <div>
        {fullName && (
          <>
            <span className="font-semibold break-all">{fullName} </span>
            <span>finished due reminder </span>
          </>
        )}
        {!fullName && <span>Due reminder </span>}"
        <span
          className="cursor-pointer font-semibold break-all hover:underline"
          onClick={() =>
            openModal(UpdateTaskModal, {
              id,
              defaultDisabled: true,
            })
          }
        >
          {name}
        </span>
        "{!fullName && <span> finished</span>}
      </div>
    );
  },
  [ActivityType.IncidentTypeCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const incidentTypeName = get(data, 'incident_type.name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}{' '}
        </span>
        <span>created event type "</span>
        <span className="font-semibold break-all">{incidentTypeName}</span>"
      </div>
    );
  },
  [ActivityType.IncidentTypeDeleted]: (data: object) => {
    const incidentTypeName = get(data, 'incident_type_instance.name', '');
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const fullName = `${firstName} ${lastName}`.trim();

    return (
      <div>
        {fullName && (
          <>
            <span className="font-semibold break-all">{fullName} </span>
            <span>deleted event type </span>
          </>
        )}
        {!fullName && <span>Event type </span>}"
        <span className="font-semibold break-all">{incidentTypeName}</span>"
        {!fullName && <span> deleted</span>}
      </div>
    );
  },
  [ActivityType.IncidentTypeUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const incidentTypeName = get(data, 'incident_type.name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}{' '}
        </span>
        <span>updated event type "</span>
        <span className="font-semibold break-all">{incidentTypeName}</span>"
      </div>
    );
  },
  [ActivityType.IncidentResultCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const incidentName = get(data, 'incident_result.name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        <span>report about incident "</span>
        <span className="font-semibold break-all">{incidentName}</span>"
      </div>
    );
  },
  [ActivityType.IncidentResultUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const incidentName = get(data, 'incident_result.name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        <span>updated event "</span>
        <span className="font-semibold break-all">{incidentName}</span>"
      </div>
    );
  },
  [ActivityType.IncidentTaskCompleted]: (data: object) => {
    const firstName = get(data, 'completed_user.first_name', '');
    const lastName = get(data, 'completed_user.last_name', '');
    const incidentName = get(data, 'incident_result.name', '');
    const taskName = get(data, 'task.name', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        <span>completed task "</span>
        <span className="font-semibold break-all">{taskName}</span>" <span>linked to "</span>
        <span className="font-semibold break-all">{incidentName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentFolderCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const folderName = get(data, 'folder_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created folder "
        <span className="font-semibold break-all">{folderName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentFolderUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const folderName = get(data, 'folder_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span>updated folder "
        <span className="font-semibold break-all">{folderName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentFolderMoved]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const folderName = get(data, 'folder_name', '');
    const old_parent_folder_name = get(data, 'old_parent_folder_name', '');
    const new_parent_folder_name = get(data, 'new_parent_folder_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span>
        moved folder "<span className="font-semibold break-all">{folderName}</span>"
        {old_parent_folder_name && (
          <>
            {' '}
            from "<span className="font-semibold break-all">{old_parent_folder_name}</span>"
          </>
        )}
        {new_parent_folder_name && (
          <>
            {' '}
            to "<span className="font-semibold break-all">{new_parent_folder_name}</span>"
          </>
        )}
      </div>
    );
  },
  [ActivityType.DocumentFolderDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const folderName = get(data, 'folder_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span>deleted folder "
        <span className="font-semibold break-all">{folderName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentUploaded]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const docName = get(data, 'document_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> uploaded document "
        <span className="font-semibold break-all">{docName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const docName = get(data, 'document_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> updated document "
        <span className="font-semibold break-all">{docName}</span>"
      </div>
    );
  },
  [ActivityType.DocumentMoved]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const docName = get(data, 'document_name', '');
    const old_parent_folder_name = get(data, 'old_parent_folder_name', '');
    const new_parent_folder_name = get(data, 'new_parent_folder_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> moved document "
        <span className="font-semibold break-all">{docName}</span>"
        {old_parent_folder_name && (
          <>
            {' '}
            from "<span className="font-semibold break-all">{old_parent_folder_name}</span>"
          </>
        )}
        {new_parent_folder_name && (
          <>
            {' '}
            to "<span className="font-semibold break-all">{new_parent_folder_name}</span>"
          </>
        )}
      </div>
    );
  },
  [ActivityType.DocumentDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const docName = get(data, 'document_name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted document "
        <span className="font-semibold break-all">{docName}</span>"
      </div>
    );
  },
  [ActivityType.IncidentResultDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const incidentName = get(data, 'incident_result.name', '');
    const incidentTypeName = get(data, 'incident_type.name', '');

    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted the "
        <span className="font-semibold break-all">{incidentName}</span>" event of type "
        <span className="font-semibold break-all">{incidentTypeName}</span>"
      </div>
    );
  },
  [ActivityType.TaskResultUpdated]: (data: object, { openModal }) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const taskName = get(data, 'task.name', '');
    const id = get(data, 'task.id', 0);
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> updated task result for task
        <span
          className="font-semibold break-all"
          onClick={() => openModal(UpdateTaskModal, { id, defaultDisabled: true })}
        >
          {' '}
          {taskName}
        </span>
      </div>
    );
  },
  [ActivityType.TaskResultDeleted]: (data: object, { openModal }) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const taskName = get(data, 'task.name', '');
    const id = get(data, 'task.id', 0);
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted task result for task
        <span
          className="font-semibold break-all"
          onClick={() => openModal(UpdateTaskModal, { id, defaultDisabled: true })}
        >
          {' '}
          {taskName}
        </span>
      </div>
    );
  },
  [ActivityType.FormCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const formName = get(data, 'form.name', '');
    const formId = get(data, 'form.id', 0);
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created form{' '}
        <span className="cursor-pointer font-semibold break-all hover:underline">
          <Link
            to={{ pathname: adminDashboardPaths.dynamic.form(formId), search: location.search }}
          >
            {formName}
          </Link>
        </span>
      </div>
    );
  },
  [ActivityType.FormUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const formName = get(data, 'form.name', '');
    const formId = get(data, 'form.id', 0);
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span>updated form{' '}
        <span className="cursor-pointer font-semibold break-all hover:underline">
          <Link
            to={{ pathname: adminDashboardPaths.dynamic.form(formId), search: location.search }}
          >
            {formName}
          </Link>
        </span>
      </div>
    );
  },
  [ActivityType.FormDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const formName = get(data, 'form.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span>deleted form{' '}
        <span className="font-semibold break-all"> {formName}</span>
      </div>
    );
  },
  [ActivityType.RegisterCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const registerName = get(data, 'label.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created register{' '}
        <span className="font-semibold break-all hover:underline">{registerName}</span>
      </div>
    );
  },
  [ActivityType.RegisterUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const registerName = get(data, 'label.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> updated register{' '}
        <span className="font-semibold break-all">{registerName}</span>
      </div>
    );
  },
  [ActivityType.RegisterDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const registerName = get(data, 'label.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted register{' '}
        <span className="font-semibold break-all">{registerName}</span>
      </div>
    );
  },
  [ActivityType.DepartmentCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const departmentName = get(data, 'department.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created department{' '}
        <span className="font-semibold break-all hover:underline">{departmentName}</span>
      </div>
    );
  },
  [ActivityType.DepartmentUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const departmentName = get(data, 'department.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> updated department{' '}
        <span className="font-semibold break-all">{departmentName}</span>
      </div>
    );
  },
  [ActivityType.DepartmentDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const departmentName = get(data, 'department.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted department{' '}
        <span className="font-semibold break-all">{departmentName}</span>
      </div>
    );
  },
  [ActivityType.ReportCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const reportName = get(data, 'report.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created report{' '}
        <span className="font-semibold break-all">{reportName}</span>
      </div>
    );
  },

  [ActivityType.ReportDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const reportName = get(data, 'report.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted report{' '}
        <span className="font-semibold break-all">{reportName}</span>
      </div>
    );
  },
  [ActivityType.ClientCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const clientName = get(data, 'client.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created client{' '}
        <span className="font-semibold break-all">{clientName}</span>
      </div>
    );
  },
  [ActivityType.ClientUpdated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const clientName = get(data, 'client.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> updated client{' '}
        <span className="font-semibold break-all">{clientName}</span>
      </div>
    );
  },
  [ActivityType.ClientDeleted]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const clientName = get(data, 'client.name', '');
    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> deleted client{' '}
        <span className="font-semibold break-all">{clientName}</span>
      </div>
    );
  },
  [ActivityType.CommunicationCreated]: (data: object) => {
    const fullName = `${get(data, 'action_taken_by_user.first_name', '')} ${get(
      data,
      'action_taken_by_user.last_name',
      '',
    )}`.trim();
    const subject = get(data, 'subject', '');
    const correspondenceType = String(get(data, 'correspondence_type', 'communication')).replaceAll(
      '_',
      ' ',
    );

    return (
      <div>
        <span className="font-semibold break-all">{fullName} </span> created {correspondenceType}{' '}
        communication record <span className="font-semibold break-all">{subject || '-'}</span>
      </div>
    );
  },
  [ActivityType.UserAdded]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const addedFirstName = get(data, 'added_user.first_name', '');
    const addedLastName = get(data, 'added_user.last_name', '');
    const role = get(data, 'role', '') as unknown as string;
    const roleLabel = role === 'admin' ? 'Admin' : 'User';
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        added{' '}
        <span className="font-semibold break-all">
          {addedFirstName} {addedLastName}
        </span>{' '}
        (role: {roleLabel}) to the organisation
      </div>
    );
  },
  [ActivityType.MeetingScheduled]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const subject = get(data, 'subject', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        scheduled meeting: "<span className="font-semibold break-all">{subject}</span>"
      </div>
    );
  },
  [ActivityType.MeetingUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const subject = get(data, 'subject', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated meeting: "<span className="font-semibold break-all">{subject}</span>"
      </div>
    );
  },
  [ActivityType.MeetingReminderSent]: (data: object) => {
    const triggeredByFirstName = get(data, 'triggered_by_user.first_name', '');
    const triggeredByLastName = get(data, 'triggered_by_user.last_name', '');
    const actionByFirstName = get(data, 'action_taken_by_user.first_name', '');
    const actionByLastName = get(data, 'action_taken_by_user.last_name', '');
    const subject = get(data, 'subject', '');
    const reminderValue = get(data, 'reminder_value', '');
    const reminderUnit = get(data, 'reminder_unit', '');
    const recipientEmails = (get(data, 'recipient_emails', []) as string[]).filter(Boolean);
    const triggeredByUserName =
      `${triggeredByFirstName} ${triggeredByLastName}`.trim() ||
      `${actionByFirstName} ${actionByLastName}`.trim();
    const reminderUnitLabel = String(reminderUnit).replace(/s$/, '');
    const reminderText =
      reminderValue && reminderUnitLabel
        ? `${reminderValue} ${reminderUnitLabel}${Number(reminderValue) === 1 ? '' : 's'}`
        : '';
    const recipientEmailsLabel =
      recipientEmails.length > 2
        ? `${recipientEmails.slice(0, -1).join(', ')}, and ${recipientEmails.at(-1)}`
        : recipientEmails.length === 2
        ? `${recipientEmails[0]} and ${recipientEmails[1]}`
        : recipientEmails[0] || '';

    return (
      <div>
        <span className="font-semibold break-all">{triggeredByUserName || 'System'}</span> sent a
        reminder {reminderText && <>{reminderText} </>}
        before meeting "<span className="font-semibold break-all">{subject || '-'}</span>"
        {recipientEmailsLabel && (
          <>
            {' '}
            to <span className="font-semibold break-all">{recipientEmailsLabel}</span>.
          </>
        )}
      </div>
    );
  },
  [ActivityType.OrganisationUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const businessName = get(data, 'business.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated organisation: "<span className="font-semibold break-all">{businessName}</span>"
      </div>
    );
  },
  [ActivityType.BatchReportCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const batchReportName = get(data, 'batch_report.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created batch report: "<span className="font-semibold break-all">{batchReportName}</span>"
      </div>
    );
  },
  [ActivityType.ReportTemplateCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const templateName = get(data, 'report_template.name', '');
    const templateType = get(data, 'report_template.type', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created report template: "<span className="font-semibold break-all">{templateName}</span>"
        {templateType && (
          <>
            {' '}
            (type: <span className="font-semibold break-all">{templateType}</span>)
          </>
        )}
      </div>
    );
  },
  [ActivityType.VaultCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const vaultName = get(data, 'vault.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created vault: "<span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.VaultUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const vaultName = get(data, 'vault.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated vault: "<span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.VaultDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const vaultName = get(data, 'vault.name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted vault: "<span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.VaultShared]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const vaultName = get(data, 'vault.name', '');
    const sharedUsersCount = get(data, 'shared_user_ids', [])?.length || 0;
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated access for vault: "<span className="font-semibold break-all">{vaultName}</span>" (
        {sharedUsersCount} {sharedUsersCount === 1 ? 'user' : 'users'} shared)
      </div>
    );
  },
  [ActivityType.SecretCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const secretName = get(data, 'secret.name', '');
    const vaultName = get(data, 'vault_name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created secret: "<span className="font-semibold break-all">{secretName}</span>" in vault "
        <span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.SecretUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const secretName = get(data, 'secret.name', '');
    const vaultName = get(data, 'vault_name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated secret: "<span className="font-semibold break-all">{secretName}</span>" in vault "
        <span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.SecretDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const secretName = get(data, 'secret.name', '');
    const vaultName = get(data, 'vault_name', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted secret: "<span className="font-semibold break-all">{secretName}</span>" in vault "
        <span className="font-semibold break-all">{vaultName}</span>"
      </div>
    );
  },
  [ActivityType.ContractCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created contract{' '}
        <span className="font-semibold break-all">{contractSubject || contractId || '-'}</span>
      </div>
    );
  },
  [ActivityType.ContractUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated contract{' '}
        <span className="font-semibold break-all">{contractSubject || contractId || '-'}</span>
      </div>
    );
  },
  [ActivityType.ContractDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted contract{' '}
        <span className="font-semibold break-all">{contractSubject || contractId || '-'}</span>
      </div>
    );
  },
  [ActivityType.ContractRestored]: createSimpleActivityRenderer('restored', 'contract', [
    'contract.contract_name',
    'contract.name',
    'subject',
    'contract_id',
  ]),
  [ActivityType.ContractFileCreated]: (data: object) => {
    const fullName = getActionUserFullName(data);
    const subject = get(data, 'subject', '');

    return (
      <div>
        {fullName && <span className="font-semibold break-all">{fullName} </span>}
        uploaded contract file for <span className="font-semibold break-all">{subject || '-'}</span>
      </div>
    );
  },
  [ActivityType.ContractFileUpdated]: createSimpleActivityRenderer('updated', 'contract file', [
    'subject',
  ]),
  [ActivityType.ContractFileDeleted]: createSimpleActivityRenderer('deleted', 'contract file', [
    'contract_file.original_file_name',
    'contract_file.file_name',
    'file.original_file_name',
    'file.name',
    'contract_file_id',
    'file_id',
  ]),
  [ActivityType.ContractPartyCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    const partyId = get(data, 'party_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created contract party <span className="font-semibold break-all">{partyId || '-'}</span>
        {contractSubject || contractId ? (
          <>
            {' '}
            for contract{' '}
            <span className="font-semibold break-all">{contractSubject || contractId}</span>
          </>
        ) : null}
      </div>
    );
  },
  [ActivityType.ContractPartyUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    const partyId = get(data, 'party_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated contract party <span className="font-semibold break-all">{partyId || '-'}</span>
        {contractSubject || contractId ? (
          <>
            {' '}
            for contract{' '}
            <span className="font-semibold break-all">{contractSubject || contractId}</span>
          </>
        ) : null}
      </div>
    );
  },
  [ActivityType.ContractPartyDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const contractSubject = get(data, 'subject', '');
    const contractId = get(data, 'contract_id', '');
    const partyId = get(data, 'party_id', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted contract party <span className="font-semibold break-all">{partyId || '-'}</span>
        {contractSubject || contractId ? (
          <>
            {' '}
            for contract{' '}
            <span className="font-semibold break-all">{contractSubject || contractId}</span>
          </>
        ) : null}
      </div>
    );
  },
  [ActivityType.PartyRestored]: createSimpleActivityRenderer('restored', 'party', partyLabelPaths),
  [ActivityType.ContractContributorAdded]: createSimpleActivityRenderer(
    'added',
    'contract contributor',
    contributorLabelPaths,
  ),
  [ActivityType.ContractContributorPermissionUpdated]: createSimpleActivityRenderer(
    'updated permissions for',
    'contract contributor',
    contributorLabelPaths,
  ),
  [ActivityType.ContractContributorRemoved]: createSimpleActivityRenderer(
    'removed',
    'contract contributor',
    contributorLabelPaths,
  ),
  [ActivityType.ContractSentForSigning]: (data: object) => {
    const fullName = getActionUserFullName(data);
    const contractLabel = getFirstExistingValue(data, contractLabelPaths);

    return (
      <div>
        {fullName && <span className="font-semibold break-all">{fullName} </span>}
        sent contract <span className="font-semibold break-all">{contractLabel}</span> for signing
      </div>
    );
  },
  [ActivityType.ContractNoteCreated]: createSimpleActivityRenderer('created', 'contract note', [
    'subject',
  ]),
  [ActivityType.ContractNoteUpdated]: createSimpleActivityRenderer('updated', 'contract note', [
    'subject',
  ]),
  [ActivityType.ContractNoteDeleted]: createSimpleActivityRenderer('deleted', 'contract note', [
    'subject',
  ]),
  [ActivityType.PartyNoteCreated]: createSimpleActivityRenderer('created', 'party note', [
    'party_note.message',
    'note.message',
    'party_note_id',
    'note_id',
  ]),
  [ActivityType.PartyNoteUpdated]: createSimpleActivityRenderer('updated', 'party note', [
    'party_note.message',
    'note.message',
    'party_note_id',
    'note_id',
  ]),
  [ActivityType.PartyNoteDeleted]: createSimpleActivityRenderer('deleted', 'party note', [
    'party_note.message',
    'note.message',
    'party_note_id',
    'note_id',
  ]),
  [ActivityType.ContractAiExtractionSucceeded]: (data: object) => {
    const contractLabel = getFirstExistingValue(data, contractLabelPaths);

    return (
      <div>
        AI extraction succeeded for contract{' '}
        <span className="font-semibold break-all">{contractLabel}</span>
      </div>
    );
  },
  [ActivityType.ContractAiExtractionFailed]: (data: object) => {
    const contractLabel = getFirstExistingValue(data, contractLabelPaths);

    return (
      <div>
        AI extraction failed for contract{' '}
        <span className="font-semibold break-all">{contractLabel}</span>
      </div>
    );
  },
  [ActivityType.JurisdictionCreated]: createSimpleActivityRenderer('created', 'jurisdiction', [
    'jurisdiction.name',
    'name',
    'subject',
  ]),
  [ActivityType.JurisdictionUpdated]: createSimpleActivityRenderer('updated', 'jurisdiction', [
    'jurisdiction.name',
    'name',
    'subject',
  ]),
  [ActivityType.JurisdictionDeleted]: createSimpleActivityRenderer('deleted', 'jurisdiction', [
    'jurisdiction.name',
    'name',
    'subject',
  ]),
  [ActivityType.PolicyCoverageCreated]: createSimpleActivityRenderer('created', 'policy coverage', [
    'policy_coverage.name',
    'policy_coverage.coverage_type',
    'coverage_type',
    'policy_coverage_id',
  ]),
  [ActivityType.PolicyCoverageUpdated]: createSimpleActivityRenderer('updated', 'policy coverage', [
    'policy_coverage.name',
    'policy_coverage.coverage_type',
    'coverage_type',
    'policy_coverage_id',
  ]),
  [ActivityType.PolicyCoverageDeleted]: createSimpleActivityRenderer('deleted', 'policy coverage', [
    'policy_coverage.name',
    'policy_coverage.coverage_type',
    'coverage_type',
    'policy_coverage_id',
  ]),
  [ActivityType.PartyCreated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const partyId = get(data, 'party_id', '');
    const subject = get(data, 'subject', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        created party <span className="font-semibold break-all">{subject || partyId || '-'}</span>
      </div>
    );
  },
  [ActivityType.PartyUpdated]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const partyId = get(data, 'party_id', '');
    const subject = get(data, 'subject', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        updated party <span className="font-semibold break-all">{subject || partyId || '-'}</span>
      </div>
    );
  },
  [ActivityType.PartyDeleted]: (data: object) => {
    const firstName = get(data, 'action_taken_by_user.first_name', '');
    const lastName = get(data, 'action_taken_by_user.last_name', '');
    const partyId = get(data, 'party_id', '');
    const subject = get(data, 'subject', '');
    return (
      <div>
        <span className="font-semibold break-all">
          {firstName} {lastName}
        </span>{' '}
        deleted party <span className="font-semibold break-all">{subject || partyId || '-'}</span>
      </div>
    );
  },
  [ActivityType.PartyContactCreated]: createSimpleActivityRenderer('created', 'party contact', [
    'subject',
  ]),
  [ActivityType.PartyContactUpdated]: createSimpleActivityRenderer('updated', 'party contact', [
    'subject',
  ]),
  [ActivityType.PartyContactDeleted]: createSimpleActivityRenderer('deleted', 'party contact', [
    'subject',
  ]),
  [ActivityType.EmailSent]: (data: object) => {
    const subject = get(data, 'subject', '');
    const recipientFirstName = get(data, 'recipient_user.first_name', '');
    const recipientLastName = get(data, 'recipient_user.last_name', '');
    const recipientEmail = get(data, 'recipient_user.email', '');
    const fallbackRecipientEmails = (get(data, 'recipient_emails', []) as string[]).filter(Boolean);
    const recipientName = `${recipientFirstName} ${recipientLastName}`.trim();
    const triggeredByUser = get(data, 'triggered_by_user', null);
    const triggeredFirstName = get(data, 'triggered_by_user.first_name', '');
    const triggeredLastName = get(data, 'triggered_by_user.last_name', '');
    const triggeredByName = `${triggeredFirstName} ${triggeredLastName}`.trim();
    const triggeredByEmail = get(data, 'triggered_by_user.email', '');

    return (
      <div>
        <span className="font-semibold break-all">
          {triggeredByName || 'System'}
          {triggeredByName && triggeredByEmail && ` (${triggeredByEmail})`}
        </span>{' '}
        {triggeredByUser ? 'triggered system email' : 'sent email'}{' '}
        <span className="font-semibold break-all">{subject}</span>
        {recipientName && (
          <>
            {' '}
            to{' '}
            <span className="font-semibold break-all">
              {recipientName}
              {recipientEmail && ` (${recipientEmail})`}
            </span>
          </>
        )}
        {!recipientName && !!fallbackRecipientEmails.length && (
          <>
            {' '}
            to <span className="font-semibold break-all">{fallbackRecipientEmails.join(', ')}</span>
          </>
        )}
      </div>
    );
  },
  [ActivityType.EmailReceived]: (data: object) => {
    const subject = get(data, 'subject', '');
    const senderName = get(data, 'sender_name', '');
    const senderEmail = get(data, 'sender_email', '');
    const senderLabel = [senderName, senderEmail ? `(${senderEmail})` : '']
      .filter(Boolean)
      .join(' ');

    return (
      <div>
        Received email <span className="font-semibold break-all">{subject || '-'}</span>
        {senderLabel && (
          <>
            {' '}
            from <span className="font-semibold break-all">{senderLabel}</span>
          </>
        )}
      </div>
    );
  },
};

interface RecentActivityProps {
  clientId?: number;
  fullHeight?: boolean;
}

const SYSTEM_FILTER_VALUE = 0;
const SYSTEM_FIXED_OPTION: SelectPaginateOption<number> = {
  value: SYSTEM_FILTER_VALUE,
  label: 'System',
};

interface RecentActivityFilters {
  dateFilter: null | [string, string];
  typeFilter: ActivityType | null;
  userFilter: SelectPaginateOption<number> | null;
  recipientFilter: SelectPaginateOption<number> | null;
}

const RecentActivity = ({ clientId, fullHeight = false }: RecentActivityProps = {}) => {
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<RecentActivityFilters>({
    dateFilter: null,
    typeFilter: null,
    userFilter: null,
    recipientFilter: null,
  });

  useEffect(() => {
    setFilters({
      dateFilter: null,
      typeFilter: null,
      userFilter: null,
      recipientFilter: null,
    });
  }, [businessId, clientId]);
  const { dateFilter, typeFilter, userFilter, recipientFilter } = filters;

  const isSystemFilter = userFilter?.value === SYSTEM_FILTER_VALUE;

  const { data, isFetching, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useTasksActivityLogsQuery({
      page: 1,
      page_size: 15,
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(dateFilter
        ? {
            from_date: dateFilter[0],
            to_date: dayjs(dateFilter[1]).add(1, 'day').format('YYYY-MM-DD'),
          }
        : {}),
      ...(clientId ? { filter_by_client_id: clientId } : {}),
      ...(userFilter && !isSystemFilter ? ({ filter_by_user_id: userFilter.value } as object) : {}),
      ...(isSystemFilter ? ({ filter_by_system: true } as object) : {}),
      ...(recipientFilter
        ? ({ filter_by_recipient_user_id: recipientFilter.value } as object)
        : {}),
    });

  const activities = (data?.pages || []).flatMap(({ data }) => data);

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handleChangeDateFilter: CalendarInputProps['rangeOnChange'] = dates => {
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      setFilters(filters => ({
        ...filters,
        dateFilter: start_date && end_date ? [start_date, end_date] : null,
      }));
      return;
    }
    setFilters(filters => ({ ...filters, dateFilter: null }));
  };
  const filtersControl = (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
      className="border-b mb-4 pb-2"
    >
      <Disclosure.Panel static>
        <div className="flex gap-4 justify-end items-end flex-wrap">
          <div>
            <CalendarInput
              range
              disabled={isFetching}
              rangeOnChange={handleChangeDateFilter}
              rangeValue={dateFilter ? [dayjs(dateFilter[0]), dayjs(dateFilter[1])] : undefined}
              className="w-max-[150px] [&>div]:py-[9px]"
            />
          </div>
          <div className="min-w-[150px]">
            <UsersSelectPaginated
              isMulti={false}
              isClearable
              disabled={isFetching}
              placeholder="Triggered by"
              value={userFilter}
              onChange={userFilter => setFilters(filters => ({ ...filters, userFilter }))}
              fixedOption={SYSTEM_FIXED_OPTION}
            />
          </div>
          <div className="min-w-[150px]">
            <UsersSelectPaginated
              isMulti={false}
              isClearable
              disabled={isFetching}
              placeholder="Email recipient"
              value={recipientFilter}
              onChange={recipientFilter => setFilters(filters => ({ ...filters, recipientFilter }))}
            />
          </div>
          <div className="min-w-[150px]">
            <Select
              isClearable
              disabled={isFetching}
              placeholder="Activity type"
              options={clientId ? clientActivitySelectOptions : activitySelectOptions}
              onChange={typeFilter => setFilters(filters => ({ ...filters, typeFilter }))}
              value={typeFilter}
            />
          </div>
        </div>
      </Disclosure.Panel>
    </Transition>
  );

  const bottomAction =
    (isLoading && <Skeleton width={77} height={11} />) ||
    (isFetchingNextPage && <Spinner />) ||
    (!isFetching && activities.length && hasNextPage && (
      <Button className="!p-0" variant="plain" onClick={handleLoadMore} disabled={!hasNextPage}>
        Show more
      </Button>
    )) ||
    null;
  return (
    <div className={classNames({ 'h-full': fullHeight })}>
      <Disclosure>
        <InfoCard
          title="Audit log"
          loading={isLoading}
          classes={{
            block: classNames('flex flex-col', { 'h-full': fullHeight }),
            content: 'flex flex-col flex-1 min-h-0',
          }}
          headerAction={
            isLoading ? (
              <div className="flex gap-3">
                <Skeleton width={15} height={15} borderRadius={15} />
                <Skeleton width={53} height={15} />
              </div>
            ) : (
              <Disclosure.Button as="div">
                <button disabled={isFetching} className="flex gap-2 items-center text-[#0369A1]">
                  <img src={FunnelBlueIcon} alt="filters" />
                  <span>Filters</span>
                </button>
              </Disclosure.Button>
            )
          }
          bottomAction={bottomAction}
        >
          {filtersControl}
          {isLoading && <RecentActivitySkeleton />}
          {!isLoading && !activities.length && (
            <div
              className={classNames('flex items-center justify-center', {
                'h-[297px]': !fullHeight,
                'flex-1': fullHeight,
              })}
            >
              No activities found
            </div>
          )}
          {!isLoading && !!activities.length && (
            <div
              className={classNames('flex flex-col gap-4', {
                'h-[297px] overflow-auto': !bottomAction && !fullHeight,
                'h-[250px] overflow-auto': !!bottomAction && !fullHeight,
                'flex-1 overflow-auto min-h-0': fullHeight,
              })}
            >
              {activities.map(({ type, data, created_at, id, created_with_ai }) => (
                <RecentActivityItem
                  data={data}
                  created_at={created_at}
                  type={type}
                  key={id}
                  created_with_ai={created_with_ai}
                />
              ))}
            </div>
          )}
        </InfoCard>
      </Disclosure>
    </div>
  );
};

interface RecentActivityItemProps {
  type: ActivityType;
  data: object;
  created_at: string;
  created_with_ai: boolean;
}

export const RecentActivityItem = ({
  type,
  data,
  created_at,
  created_with_ai,
}: RecentActivityItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const renderText = mapActivityTypeToGetTextFunction[type];
  return (
    <div className="flex gap-4">
      <div className="flex w-[36px] h-[36px] rounded-full bg-[#f5f5f5] p-2 shrink-0">
        <img src={mapActivityTypeToIcon[type] || HistoryGrayIcon} alt={type} />
      </div>
      <div className="flex flex-col">
        <div>{renderText ? renderText(data, { openModal }) : <span>{type}</span>}</div>
        <div className="text-[12px] text-[#737373]">
          {dayjs(created_at).format('DD-MM-YYYY, HH:mm')}
        </div>
        <CreatedByAiLabel created_with_ai={created_with_ai} />
      </div>
    </div>
  );
};

export default RecentActivity;
